<template>
  <div :class="[$style.chartContainer, isDark ? $style.dark : $style.light]">
    <line-chart :data="chartData" :options="chartOptions" />
  </div>
</template>

<script>
import { defineComponent } from "vue";
import { Line } from "vue-chartjs";
import {
  Chart as ChartJs,
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js";

ChartJs.register(
  Title,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement
);

export default defineComponent({
  name: "SalesComparisonChart",
  props: {
    monthlySales: {
      type: Object,
      required: true,
    },
    theme: {
      type: String,
      default: "auto", // Accepts 'light', 'dark', or 'auto'
      validator: (val) => ["light", "dark", "auto"].includes(val),
    },
  },
  components: {
    LineChart: Line,
  },
  data() {
    return {
      systemTheme: "light",
      chartData: {
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        datasets: [
          {
            label: "Offline Sales",
            data: [],
            borderColor: "#4B9CD3",
            backgroundColor: "rgba(75, 156, 211, 0.2)",
            tension: 0.4,
            fill: true,
          },
          {
            label: "Online Sales",
            data: [],
            borderColor: "#FFD700",
            backgroundColor: "rgba(255, 215, 0, 0.2)",
            tension: 0.4,
            fill: true,
          },
        ],
      },
    };
  },
  computed: {
    isDark() {
      return this.theme === "auto"
        ? this.systemTheme === "dark"
        : this.theme === "dark";
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            labels: {
              color: this.isDark ? "#e0e0e0" : "#222",
            },
            position: "bottom",
          },
          title: {
            display: true,
            text: "Sales Comparison (Offline vs. Online)",
            color: this.isDark ? "#f0f0f0" : "#222",
            font: { size: 16 },
            padding: { bottom: 20 },
          },
          tooltip: {
            enabled: true,
            backgroundColor: this.isDark ? "#333" : "#fff",
            titleColor: this.isDark ? "#fff" : "#000",
            bodyColor: this.isDark ? "#ccc" : "#333",
            borderColor: this.isDark ? "#555" : "#ccc",
            borderWidth: 1,
          },
        },
        scales: {
          x: {
            ticks: {
              color: this.isDark ? "#bbb" : "#111",
            },
            grid: {
              color: this.isDark ? "#444" : "#eee",
            },
          },
          y: {
            beginAtZero: true,
            ticks: {
              color: this.isDark ? "#bbb" : "#111",
            },
            grid: {
              color: this.isDark ? "#444" : "#eee",
            },
          },
        },
      };
    },
  },
  mounted() {
    const darkQuery = window.matchMedia("(prefers-color-scheme: dark)");
    this.systemTheme = darkQuery.matches ? "dark" : "light";
    darkQuery.addEventListener("change", (e) => {
      this.systemTheme = e.matches ? "dark" : "light";
    });
  },
  watch: {
    monthlySales: {
      handler(newVal) {
        this.updateChart(newVal);
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    updateChart(data) {
      const offlineSales = Array(12).fill(0);
      const onlineSales = Array(12).fill(0);

      Object.entries(data).forEach(([month, val]) => {
        const index = parseInt(month, 10) - 1;
        offlineSales[index] = val.offline || 0;
        onlineSales[index] = val.online || 0;
      });

      this.chartData.datasets[0].data = offlineSales;
      this.chartData.datasets[1].data = onlineSales;
    },
  },
});
</script>

<style module>
.chartContainer {
  width: 100%;
  height: 400px;
  padding: 1rem;
  border-radius: 12px;
  transition:
    background-color 0.3s ease,
    color 0.3s ease;
}
.light {
  background-color: transparent;
  color: #111111;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
}
.dark {
  background-color: transparent;
  color: #f0f0f0;
  box-shadow: 0 0 10px rgba(255, 255, 255, 0.05);
}
</style>
